/**
 * Live microphone level — ONE implementation for every recorder in the app
 * (voice clone, dictation, dictate-to-prompt, realtime agent). Attaches an
 * AnalyserNode to a MediaStream and reports the RMS level (0..1) each frame.
 *
 * Returns a stop() that tears everything down; never throws (the meter is a
 * convenience — recording must never depend on it).
 */
export function attachLevelMeter(stream: MediaStream, onLevel: (level: number) => void): () => void {
  let raf: number | null = null;
  let ctx: AudioContext | null = null;
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return () => undefined;
    ctx = new Ctx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      // RMS 0..1 with a gentle boost so normal speech fills most of a meter.
      onLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  } catch {
    // no-op meter
  }
  return () => {
    if (raf != null) cancelAnimationFrame(raf);
    raf = null;
    if (ctx) void ctx.close().catch(() => undefined);
    ctx = null;
    onLevel(0);
  };
}

/** Below this RMS after a couple of seconds the mic is most likely not being heard. */
export const MIC_SILENT_LEVEL = 0.03;

/** Below this level (sustained ~3 s while speaking) the take will be too quiet to clone well. */
export const MIC_QUIET_LEVEL = 0.12;

/** At/above this level the input is at the ceiling — sustained ~1 s means the take is clipping. */
export const MIC_CLIP_LEVEL = 0.97;
